import Dexie, { type Table } from 'dexie';
import { logStorePersistFailure } from '../useSessionRecordingStore/lib/logStorePersistFailure';
import { logger } from '../useSessionRecordingStore/lib/logger';
import { dbV2 } from './dbV2';
import { withReopen } from './lifecycle';
import { packBlob, unpackBlob } from './packBlob';
import {
  forgetParentGuardSuppression,
  parentAccepted,
  reportCommittedParentRepair,
  resetParentGuardSuppression,
  resolveParentForChildWrite,
  type ChildWriteKind,
} from './parentGuard';
import {
  forgetRecordingChunks,
  peekChunkStatus,
  rememberChunkStatus,
  rememberRecordingAbandoned,
  resetRecordingProgressMirror,
} from './recordingProgressMirror';
import {
  MIRRORED_FIELDS,
  forgetRecordingRow,
  isMemoryBackedRecording,
  listRecordingRows,
  markHealedPartsPersisted,
  markRecordingFieldPersisted,
  markRecordingFieldUnpersisted,
  markRecordingRowUnpersisted,
  patchRecordingRow,
  peekRecordingRow,
  pinTransportInMirror,
  rememberRecordingRow,
  resetRecordingRowCache,
  type UnpersistedPart,
} from './recordingRowCache';
import {
  forgetRetainedRecording,
  hasDrainableRetainedRecordingChunks,
  isDrainableRetainedChunk,
  isRetainedChunk,
  maxRetainedChunkIndex,
  resetRetryBuffer,
} from './retryBuffer';
import {
  acceptsTransportPin,
  aggregateRecordingProgress,
  chunkPk,
  hasIdempotentChunkDelivery,
  isFinal,
  isSettled,
  isUploadCompleteFrom,
  recordingPk,
  type BackupRow,
  type ChunkKey,
  type ChunkMetaInput,
  type ChunkRow,
  type RecordingKey,
  type RecordingProgress,
  type RecordingRow,
  type RecordingUploadTransport,
} from './types';
import {
  assertWriteAllowed,
  captureEpoch,
  isMemoryOnlyArmed,
  isWriteFenceStaleError,
  subscribeFenceClosed,
} from './writeFence';

// A closed fence means logout is underway; drop the mirrored rows with it.
subscribeFenceClosed(() => {
  resetRecordingRowCache();
  resetRecordingProgressMirror();
  resetRetryBuffer();
  resetParentGuardSuppression();
});

/**
 * The mirror may only hold what the fence would accept: a failing store still
 * mirrors, a closed fence does not.
 */
const mirrorAllowed = (epoch: number): boolean => {
  try {
    assertWriteAllowed(epoch);
    return true;
  } catch {
    return false;
  }
};

const keyDetails = (key: RecordingKey & Partial<ChunkKey>) => ({
  sessionId: key.sessionId,
  recordingId: key.recordingId,
  ...(key.index !== undefined ? { index: key.index } : {}),
});

/** All chunk-table rows of one recording, via primary-key range. */
const chunkRangeOf = <T>(table: Table<T, [string, string, number]>, key: RecordingKey) =>
  table
    .where(':id')
    .between(
      [key.sessionId, key.recordingId, Dexie.minKey],
      [key.sessionId, key.recordingId, Dexie.maxKey]
    );

// ---------------------------------------------------------------------------
// Recordings
// ---------------------------------------------------------------------------

export interface CreateRecordingInput extends RecordingKey {
  userRefId: string;
  useTranscriptionServiceV2: boolean;
  startDate?: Date;
  createdAt?: number;
  importedFromLegacy?: boolean;
}

export const createRecording = async (input: CreateRecordingInput) => {
  const epoch = captureEpoch();
  const startDate = input.startDate ?? new Date();
  // Built before the transaction so an unreadable store still yields a mirror row.
  let created: RecordingRow = {
    sessionId: input.sessionId,
    recordingId: input.recordingId,
    userRefId: input.userRefId,
    startDate,
    lastChunkIndex: null,
    useTranscriptionServiceV2: input.useTranscriptionServiceV2,
    createdAt: input.createdAt ?? startDate.getTime(),
    backupRequired: true,
    ...(input.importedFromLegacy ? { importedFromLegacy: true } : {}),
  };
  try {
    const result = await withReopen(() =>
      dbV2.transaction('rw', dbV2.recordings, async () => {
        assertWriteAllowed(epoch);
        // The transport pin is first-write-wins for the recording's lifetime, so a
        // re-entrant create keeps it and resets everything else. One transaction, so
        // a concurrent pin cannot slip between the read and the put.
        const existing = await dbV2.recordings.get(
          recordingPk({ sessionId: input.sessionId, recordingId: input.recordingId })
        );
        if (existing?.uploadTransport) {
          created = { ...created, uploadTransport: existing.uploadTransport };
        }
        return dbV2.recordings.put(created);
      })
    );
    rememberRecordingRow(created);
    return result;
  } catch (err) {
    // A storage failure still mirrors — memory is the only copy the upload path
    // gets. A closed fence does not: logout must leave nothing uploadable.
    if (!isWriteFenceStaleError(err)) {
      // A failure before the read carries no transport, and a transport-less
      // mirror would upload over HTTP against a pinned delivery.
      const uploadTransport =
        created.uploadTransport ?? peekRecordingRow(created)?.uploadTransport;
      rememberRecordingRow(uploadTransport ? { ...created, uploadTransport } : created, {
        persisted: false,
      });
    }
    throw err;
  }
};

/** Higher of two known boundaries; `null` only when neither is known. */
const maxBoundary = (a: number | null, b: number | null): number | null =>
  a === null ? b : b === null ? a : Math.max(a, b);

/**
 * The boundary the heal must write: the highest index anything can prove, so a
 * recording cannot complete on a lower one. `null` stays `null` — a boundary
 * invented from a retained chunk would finish a capture that is still running.
 */
const healedBoundary = (
  key: RecordingKey,
  storedLast: number | null,
  mirroredLast: number | null
): number | null => {
  const declared = maxBoundary(storedLast, mirroredLast);
  return declared === null ? null : maxBoundary(declared, maxRetainedChunkIndex(key));
};

/** `null` leaves the index alone: someone else still owes its row. */
const planHealedChunk = (key: ChunkKey): 'uploaded' | 'abandoned' | null => {
  if (peekChunkStatus(key) === 'uploaded') return 'uploaded';
  // The drain still owns this blob and will write the row when it lands.
  // Exhausted is the opposite: the blob lives only in memory, so a durable
  // 'failed' row would send recovery after bytes that were never stored.
  if (isRetainedChunk(key)) return isDrainableRetainedChunk(key) ? null : 'abandoned';
  // Still in flight — mark/fail writes its row when the upload settles.
  if (peekChunkStatus(key) === 'pending') return null;
  return 'abandoned';
};

/** Indexes of one recording's chunk rows, read as keys — this pass wants no bodies. */
const storedChunkIndexes = async (key: RecordingKey): Promise<Set<number>> =>
  new Set(
    (await chunkRangeOf(dbV2.chunks, key).primaryKeys()).map(([, , index]) => index)
  );

/**
 * Newest write across a recording's chunk rows, falling back to `createdAt`
 * when it has none. Read per recovery candidate, so the range stays bounded.
 */
const lastRecordingActivity = async (recording: RecordingRow): Promise<number> => {
  // Gap rows are the drain's own writes, not the owner's.
  const chunks = (await chunkRangeOf(dbV2.chunks, recording).toArray()).filter(
    (chunk) => !chunk.syntheticGap
  );
  if (chunks.length === 0) return recording.createdAt;
  return chunks.reduce(
    (latest, chunk) => Math.max(latest, chunk.producedAt, chunk.updatedAt),
    0
  );
};

/**
 * The recording's newest write if it is still inside the activity window, else
 * null. A stopped, fully-indexed row skips the read when a re-send of its tail
 * is harmless; a V1 tail is not, so it waits like a crashed row.
 */
const stillActiveAt = async (
  recording: RecordingRow,
  inactiveSince: number
): Promise<number | null> => {
  if (
    recording.endDate !== undefined &&
    recording.lastChunkIndex !== null &&
    hasIdempotentChunkDelivery(recording)
  ) {
    return null;
  }
  const lastActivityAt = await lastRecordingActivity(recording);
  return lastActivityAt <= inactiveSince ? null : lastActivityAt;
};

/** Indexes across 0..last that no stored row covers. */
const missingChunkIndexes = (present: ReadonlySet<number>, lastChunkIndex: number) => {
  const missing: number[] = [];
  for (let index = 0; index <= lastChunkIndex; index++) {
    if (!present.has(index)) missing.push(index);
  }
  return missing;
};

/**
 * Plants the chunk rows the store is missing across 0..last. Without them a
 * written boundary strands completion on indexes no writer will fill. Runs on
 * every heal flush, so it reads keys rather than rows.
 */
const plantHealedChunkRows = async (
  { sessionId, recordingId }: RecordingKey,
  lastChunkIndex: number
): Promise<void> => {
  // Narrowed, not spread: a chunk row carries the key and nothing else.
  const key: RecordingKey = { sessionId, recordingId };
  const missing = missingChunkIndexes(await storedChunkIndexes(key), lastChunkIndex);
  if (missing.length === 0) return;
  const now = Date.now();
  const planted: ChunkRow[] = [];
  for (const index of missing) {
    const status = planHealedChunk({ ...key, index });
    if (status === null) continue;
    planted.push({
      ...key,
      index,
      id: `heal-${status}-${index}`,
      status,
      wasMissingChunk: true,
      size: 0,
      duration: 0,
      producedAt: 0,
      attempts: 0,
      updatedAt: now,
      hasBlob: false,
      // Same marker normalize plants, so late real bytes can still replace it.
      ...(status === 'abandoned' ? { syntheticGap: true as const } : {}),
    });
  }
  if (planted.length > 0) await dbV2.chunks.bulkPut(planted);
};

/** What one heal flush landed: the parts it wrote, and the boundary the store now holds. */
interface HealFlush {
  parts: UnpersistedPart[];
  boundary: number | null;
}

/**
 * Completion reads the mirror first, so a boundary raised in the store must rise
 * here too. Upward only: a concurrent `setLastChunkIndex` may hold a newer
 * boundary, and clobbering it would drop a chunk from the note.
 */
const raiseMirroredBoundary = (key: RecordingKey, boundary: number): void => {
  const mirrored = peekRecordingRow(key);
  if (!mirrored) return;
  if (mirrored.lastChunkIndex !== null && mirrored.lastChunkIndex >= boundary) return;
  patchRecordingRow(key, { lastChunkIndex: boundary });
};

/**
 * Heal-flush upsert: the mirror only fills gaps a stored row leaves, and a field
 * absent from both is deleted, never resurrected.
 *
 * The boundary rises to the highest anything can prove, and its missing chunk
 * rows are planted in the same transaction.
 *
 * Returns the parts it discharged — only what it actually put, or completion
 * would run on a boundary this flush never wrote.
 */
export const persistMirroredRow = async (
  row: RecordingRow
): Promise<UnpersistedPart[]> => {
  const epoch = captureEpoch();
  const flushed = await withReopen(() =>
    dbV2.transaction('rw', dbV2.recordings, dbV2.chunks, async (): Promise<HealFlush> => {
      assertWriteAllowed(epoch);
      const stored = await dbV2.recordings.get(recordingPk(row));
      if (!stored) {
        if (!peekRecordingRow(row)) return { parts: [], boundary: null };
        const boundary = healedBoundary(row, null, row.lastChunkIndex);
        await dbV2.recordings.put(
          boundary === null ? row : { ...row, lastChunkIndex: boundary }
        );
        if (boundary !== null) await plantHealedChunkRows(row, boundary);
        return { parts: ['row', ...MIRRORED_FIELDS], boundary };
      }
      const boundary = healedBoundary(row, stored.lastChunkIndex, row.lastChunkIndex);
      const raisesBoundary = boundary !== null && boundary !== stored.lastChunkIndex;
      // Before the early return: a settled row can still be missing chunk
      // rows, and that shape is what cleanup can never delete.
      if (boundary !== null) await plantHealedChunkRows(row, boundary);
      const gaps = Object.keys(row).filter((field) => !(field in stored));
      if (gaps.length === 0 && !raisesBoundary) return { parts: [], boundary };
      await dbV2.recordings.put({
        ...row,
        ...stored,
        ...(raisesBoundary ? { lastChunkIndex: boundary } : {}),
      });
      const filled: UnpersistedPart[] = MIRRORED_FIELDS.filter((field) =>
        gaps.includes(field)
      );
      // The stored row won every other field, so only the boundary override and
      // the gaps carry a mirrored value into the database.
      if (raisesBoundary && !filled.includes('lastChunkIndex'))
        filled.push('lastChunkIndex');
      return { parts: filled, boundary };
    })
  );
  // A part discharges only if the mirror's value is the one that landed, so a
  // boundary a concurrent `setLastChunkIndex` overtook stays owed.
  if (flushed.boundary !== null) raiseMirroredBoundary(row, flushed.boundary);
  return markHealedPartsPersisted(
    flushed.boundary === null ? row : { ...row, lastChunkIndex: flushed.boundary },
    flushed.parts
  );
};

/**
 * Why a transport pin did not land. `failed` means the store refused the write,
 * so nothing arbitrated the pin and the caller must not upload under it.
 */
export type SetRecordingUploadTransportResult =
  | 'pinned'
  | 'missing'
  | 'rejected'
  | 'failed';

/**
 * Transaction faults that leave a working store behind. `withReopen` retries
 * only connection faults, so these reach the caller after a single abort.
 */
const TRANSIENT_TRANSACTION_ERRORS = new Set([
  'AbortError',
  'PrematureCommitError',
  'TimeoutError',
  'TransactionInactiveError',
]);

const isTransientTransactionError = (err: unknown): boolean =>
  err instanceof Error && TRANSIENT_TRANSACTION_ERRORS.has(err.name);

/** A refusal carries the transport the database holds, so the mirror can adopt it. */
type PinAttempt =
  | { result: 'pinned' }
  | { result: 'missing' }
  | { result: 'rejected'; stored: RecordingUploadTransport };

const pinTransportInDatabase = (
  key: RecordingKey,
  uploadTransport: RecordingUploadTransport
): Promise<PinAttempt> => {
  const epoch = captureEpoch();
  // One transaction: the first-write-wins check and the write must be atomic,
  // or two concurrent pins could both pass the check and both land.
  return withReopen(() =>
    dbV2.transaction('rw', dbV2.recordings, async (): Promise<PinAttempt> => {
      assertWriteAllowed(epoch);
      const row = await dbV2.recordings.get(recordingPk(key));
      if (!row) {
        logger.warn({
          event_type: 'LEDGER_UPLOAD_TRANSPORT_RECORDING_MISSING',
          details: { ...keyDetails(key), kind: uploadTransport.kind },
        });
        return { result: 'missing' };
      }
      const current = row.uploadTransport;
      // Only an existing decision can refuse one, so `current` is always set here.
      if (current && !acceptsTransportPin(current, uploadTransport)) {
        logger.warn({
          event_type: 'LEDGER_UPLOAD_TRANSPORT_CHANGE_REJECTED',
          details: {
            ...keyDetails(key),
            currentKind: current.kind,
            attemptedKind: uploadTransport.kind,
          },
        });
        return { result: 'rejected', stored: current };
      }
      const updated = await dbV2.recordings.update(recordingPk(key), {
        uploadTransport,
      });
      if (!updated) {
        logger.warn({
          event_type: 'LEDGER_UPLOAD_TRANSPORT_RECORDING_MISSING',
          details: { ...keyDetails(key), kind: uploadTransport.kind },
        });
        return { result: 'missing' };
      }
      return { result: 'pinned' };
    })
  );
};

const applyPinResult = (
  key: RecordingKey,
  uploadTransport: RecordingUploadTransport,
  attempt: PinAttempt
): SetRecordingUploadTransportResult => {
  if (attempt.result === 'pinned') {
    patchRecordingRow(key, { uploadTransport });
    // Only the transport landed: a boundary or stop time refused earlier is
    // still owed to the heal.
    markRecordingFieldPersisted(key, 'uploadTransport');
    return attempt.result;
  }
  if (attempt.result === 'missing') {
    // A row the database never accepted lives only in the mirror, so forgetting
    // it would destroy the recording's only copy. Pin in memory instead.
    const mirrored = pinTransportInMirror(key, uploadTransport);
    if (mirrored !== 'missing') {
      markRecordingRowUnpersisted(key);
      logger.warn({
        event_type: 'LEDGER_UPLOAD_TRANSPORT_PINNED_IN_MEMORY',
        details: { ...keyDetails(key), kind: uploadTransport.kind, result: mirrored },
      });
      return mirrored;
    }
    return attempt.result;
  }
  // The refusal read the row, so adopt the transport it holds — dropping the row
  // would take a stop time or boundary the mirror may be the only copy of.
  patchRecordingRow(key, { uploadTransport: attempt.stored });
  markRecordingFieldPersisted(key, 'uploadTransport');
  return attempt.result;
};

/**
 * Pins or refreshes the recording's upload transport. First write wins for the
 * recording's lifetime: an existing pin accepts only a same-delivery ticket
 * refresh, never a switch of transport or delivery.
 */
export const setRecordingUploadTransport = async (
  key: RecordingKey,
  uploadTransport: RecordingUploadTransport
): Promise<SetRecordingUploadTransportResult> => {
  let lastError: unknown;
  // Two attempts: a one-off abort on a healthy store must not read as storage
  // death, or 'pinned' claims a durable pin recovery will not find.
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return applyPinResult(
        key,
        uploadTransport,
        await pinTransportInDatabase(key, uploadTransport)
      );
    } catch (err) {
      // A closed fence still refuses; logout must not pin anything.
      if (isWriteFenceStaleError(err)) throw err;
      lastError = err;
      if (!isTransientTransactionError(err)) break;
    }
  }

  if (isTransientTransactionError(lastError)) {
    logger.warn({
      event_type: 'LEDGER_UPLOAD_TRANSPORT_PIN_FAILED',
      details: { ...keyDetails(key), kind: uploadTransport.kind },
    });
    return 'failed';
  }

  // Storage is unusable, so the mirror arbitrates instead. Temporal
  // delivery needs the pin, not its durability — see pinTransportInMirror.
  const result = pinTransportInMirror(key, uploadTransport);
  if (result === 'pinned') markRecordingFieldUnpersisted(key, 'uploadTransport');
  logger.warn({
    event_type: 'LEDGER_UPLOAD_TRANSPORT_PINNED_IN_MEMORY',
    details: { ...keyDetails(key), kind: uploadTransport.kind, result },
  });
  return result;
};

export const getRecordingRow = (key: RecordingKey) =>
  withReopen(() => dbV2.recordings.get(recordingPk(key)));

export const getCurrentRecordingRow = (
  sessionId: string
): Promise<RecordingRow | undefined> =>
  withReopen(async () => {
    const rows = await dbV2.recordings.where('sessionId').equals(sessionId).toArray();
    return rows.sort((a, b) => b.createdAt - a.createdAt)[0];
  });

/**
 * Live stop and error cleanup must pass the active recordingId.
 * Omit it only when intentionally targeting the supplied session's latest recording.
 */
export const setRecordingEndDate = (sessionId: string, recordingId?: string) => {
  const epoch = captureEpoch();
  const endDate = new Date();
  // Memory first, so a store that rejects the write still has the stop time —
  // the backup upload reads the mirror ahead of the database.
  const mirrored = recordingId
    ? peekRecordingRow({ sessionId, recordingId })
    : listRecordingRows()
        .filter((row) => row.sessionId === sessionId)
        .sort((a, b) => b.createdAt - a.createdAt)[0];
  if (mirrored) patchRecordingRow(mirrored, { endDate });
  return withReopen(() =>
    dbV2.transaction('rw', dbV2.recordings, async () => {
      assertWriteAllowed(epoch);
      const row = recordingId
        ? await dbV2.recordings.get([sessionId, recordingId])
        : (await dbV2.recordings.where('sessionId').equals(sessionId).toArray()).sort(
            (a, b) => b.createdAt - a.createdAt
          )[0];
      if (!row) {
        // Read before the mark: marking unpersisted would make every miss look
        // memory-backed and silence a durable recording's genuine orphan.
        const memoryOnly = mirrored !== undefined && isMemoryBackedRecording(mirrored);
        if (mirrored) markRecordingRowUnpersisted(mirrored);
        logStorePersistFailure(
          {
            event_type: 'LEDGER_END_DATE_RECORDING_MISSING',
            details: { sessionId, recordingId },
          },
          { memoryOnly, level: 'warn' }
        );
        return;
      }
      await dbV2.recordings.update([row.sessionId, row.recordingId], { endDate });
      markRecordingFieldPersisted(row, 'endDate');
    })
  ).catch((err) => {
    // A closed fence still refuses; logout must not keep a stop time alive.
    if (isWriteFenceStaleError(err) || !mirrored) throw err;
    // The mirror holds the stop time and chunks upload from it, so an unusable
    // store must not fail the stop — that would cost the note as well.
    markRecordingFieldUnpersisted(mirrored, 'endDate');
    logger.warn({
      event_type: 'LEDGER_END_DATE_KEPT_IN_MEMORY',
      details: {
        sessionId,
        recordingId,
        error: err instanceof Error ? err.message : String(err),
      },
    });
  });
};

export const setLastChunkIndex = (key: RecordingKey, index: number) => {
  const epoch = captureEpoch();
  const mirrored = mirrorAllowed(epoch);
  if (mirrored) patchRecordingRow(key, { lastChunkIndex: index });
  return withReopen(async () => {
    assertWriteAllowed(epoch);
    const updated = await dbV2.recordings.update(recordingPk(key), {
      lastChunkIndex: index,
    });
    if (updated) {
      markRecordingFieldPersisted(key, 'lastChunkIndex');
      return;
    }
    // A miss is an `update()` that matched nothing, not a fence refusal.
    // Provenance read before the mark, so a durable recording's orphan warns.
    const memoryOnly = isMemoryBackedRecording(key);
    markRecordingRowUnpersisted(key);
    logStorePersistFailure(
      {
        event_type: 'LEDGER_LAST_CHUNK_INDEX_RECORDING_MISSING',
        details: { ...keyDetails(key), index },
      },
      { memoryOnly, level: 'warn' }
    );
  }).catch((err: unknown) => {
    // The mirror holds a boundary the store refused, and a mirror-only boundary
    // strands completion forever — re-arm the heal for this field.
    if (mirrored && !isWriteFenceStaleError(err)) {
      markRecordingFieldUnpersisted(key, 'lastChunkIndex');
    }
    throw err;
  });
};

export interface RecoveryNormalizationOptions {
  /** Age cut for callers with no lock to prove capture is idle. */
  createdBefore?: number;
  /** Newest write a crashed-shaped row may carry and still take a boundary. */
  inactiveSince?: number;
  /** Told the newest write of each row `inactiveSince` kept out of the pass. */
  onDeferred?: (lastActivityAt: number) => void;
}

/**
 * How long after its stop a settled recording may still have a detached backup
 * persist in flight. Measured from `endDate`, not `createdAt`: a long consult is
 * old the moment it starts, and the capture lock does not cover detached work.
 */
const BACKUP_SETTLE_GRACE_MS = 60_000;

/**
 * Finalizes rows left incomplete by a renderer crash, and repairs settled rows
 * cleanup can neither delete nor stop warning about: holes in the chunk
 * sequence, or a backup row nothing ever wrote.
 *
 * Missing indexes become terminal gap rows, except where the retry buffer still
 * holds the audio — that hole a later pass can fill for real. Returns only
 * crashed rows; a settled one already ran its completion and would re-notify.
 */
export const normalizeIncompleteRecordingsForRecovery = async (
  currentUserRefId: string,
  options: RecoveryNormalizationOptions = {}
): Promise<RecordingKey[]> => {
  const epoch = captureEpoch();
  const { normalized, repaired } = await withReopen(() =>
    dbV2.transaction('rw', dbV2.recordings, dbV2.chunks, dbV2.backups, async () => {
      assertWriteAllowed(epoch);
      // Liveness first, and the owner filter is only half of it: `createdBefore`
      // and `inactiveSince` are what stand between a gap row and a recording
      // that is still capturing.
      const recordings = (await dbV2.recordings.toArray()).filter(
        (row) =>
          row.userRefId === currentUserRefId &&
          (options.createdBefore === undefined || row.createdAt <= options.createdBefore)
      );
      const now = Date.now();
      const normalized: RecordingKey[] = [];
      let repaired = 0;
      // A memory-only fence refuses every put and one refusal aborts the whole
      // transaction, so the settled-row repair would cost the crashed-row pass
      // with it.
      const repairsSettledRows = !isMemoryOnlyArmed();

      for (const recording of recordings) {
        const key = {
          sessionId: recording.sessionId,
          recordingId: recording.recordingId,
        };
        const crashed = !recording.endDate || recording.lastChunkIndex === null;
        if (!crashed && !repairsSettledRows) continue;
        // Only a crashed-shaped row can still be a live recording, so it is the
        // only one that pays for the activity read before taking a boundary.
        if (crashed && options.inactiveSince !== undefined) {
          const activeAt = await stillActiveAt(recording, options.inactiveSince);
          if (activeAt !== null) {
            options.onDeferred?.(activeAt);
            continue;
          }
        }

        // Keys, not rows: this pass reads nothing off a chunk but its index,
        // and a settled healthy row is the overwhelmingly common case.
        const presentIndexes = await storedChunkIndexes(key);
        const observedLastIndex = [...presentIndexes].reduce(
          (last, index) => Math.max(last, index),
          -1
        );
        const lastChunkIndex = Math.max(
          recording.lastChunkIndex ?? -1,
          observedLastIndex
        );
        const missing = missingChunkIndexes(presentIndexes, lastChunkIndex).filter(
          // The drain still owns this blob and writes its real row when it lands;
          // a gap row here would foreclose deliverable audio.
          (index) => !isDrainableRetainedChunk({ ...key, index })
        );

        // Cheap screen before the backup read: a settled row with a whole
        // sequence and no backup requirement is finished business.
        if (!crashed && missing.length === 0 && !recording.backupRequired) continue;

        const backup = await dbV2.backups.get(recordingPk(key));
        // "No backup row" is only a ruling once nothing can still be writing one:
        // a crashed row's writer is gone, a settled one's may not be.
        const backupRulingReady =
          crashed ||
          recording.endDate === undefined ||
          now - recording.endDate.getTime() > BACKUP_SETTLE_GRACE_MS;
        // Stuck: cleanup can neither delete the row nor stop warning about it.
        const stuck =
          missing.length > 0 ||
          (recording.backupRequired && !backup && backupRulingReady);
        // A settled, whole row is finished business; rewriting it every recovery
        // would churn `backupRequired` for nothing.
        if (!crashed && !stuck) continue;

        if (missing.length > 0) {
          const gaps: ChunkRow[] = missing.map((index) => ({
            ...key,
            index,
            id: `recovery-gap-${index}`,
            status: 'abandoned',
            wasMissingChunk: true,
            size: 0,
            duration: 0,
            producedAt: 0,
            attempts: 0,
            updatedAt: now,
            hasBlob: false,
            syntheticGap: true,
          }));
          await dbV2.chunks.bulkPut(gaps);
        }

        await dbV2.recordings.put({
          ...recording,
          endDate: recording.endDate ?? new Date(now),
          lastChunkIndex,
          // Until the ruling is ready the requirement stands, or cleanup could
          // delete the recording out from under a backup still being written.
          backupRequired: backupRulingReady
            ? backup !== undefined
            : recording.backupRequired,
        });
        if (crashed) normalized.push(key);
        else repaired += 1;
      }

      return { normalized, repaired };
    })
  );

  if (normalized.length > 0 || repaired > 0) {
    logger.info({
      event_type: 'LEDGER_CRASHED_RECORDINGS_NORMALIZED',
      details: { normalized: normalized.length, repaired },
    });
  }
  return normalized;
};

/**
 * The parent guard's two halves bound to one child write. `accept` runs inside
 * the transaction; a refusal costs no delivery because the upload path runs
 * independently. `afterCommit` runs once the write lands — reporting a repair
 * inside the transaction would claim a parent row an abort then rolled back,
 * and `withReopen` can run the body twice.
 */
const parentGuardFor = (key: RecordingKey, childKind: ChildWriteKind) => {
  let repaired: RecordingRow | undefined;
  return {
    accept: async (): Promise<boolean> => {
      repaired = undefined;
      const parent = await resolveParentForChildWrite(key, childKind);
      if (parent.outcome === 'repaired') repaired = parent.recording;
      return parentAccepted(parent);
    },
    afterCommit: (): void => {
      if (!repaired) return;
      // The repair wrote the whole mirrored row, so every field it carried is
      // discharged with it — a re-armed newer value stays owed.
      markHealedPartsPersisted(repaired, ['row', ...MIRRORED_FIELDS]);
      reportCommittedParentRepair(key, childKind);
    },
  };
};

// ---------------------------------------------------------------------------
// Chunks
// ---------------------------------------------------------------------------

export const addChunk = async (meta: ChunkMetaInput, blob: Blob) => {
  const epoch = captureEpoch();
  if (mirrorAllowed(epoch)) rememberChunkStatus(meta, 'pending');
  // Pack before opening a transaction because blob.arrayBuffer() yields.
  const stored = await packBlob(blob);
  const parent = parentGuardFor(meta, 'chunk');

  await withReopen(() =>
    // `recordings` joins the transaction so the parent cannot be cascade-deleted
    // between the check and the child write.
    dbV2.transaction('rw', dbV2.recordings, dbV2.chunks, dbV2.chunkBlobs, async () => {
      assertWriteAllowed(epoch);
      if (!(await parent.accept())) return;
      const existing = await dbV2.chunks.get(chunkPk(meta));
      if (existing) {
        if (existing.syntheticGap) {
          const withoutSyntheticGap = { ...existing };
          delete withoutSyntheticGap.syntheticGap;
          const status =
            existing.status === 'uploaded' || existing.status === 'failed'
              ? existing.status
              : 'pending';
          await dbV2.chunks.put({
            ...withoutSyntheticGap,
            ...meta,
            status,
            wasMissingChunk: false,
            updatedAt: Date.now(),
            hasBlob: true,
          });
          await dbV2.chunkBlobs.put({
            sessionId: meta.sessionId,
            recordingId: meta.recordingId,
            index: meta.index,
            blob: stored,
          });
          return;
        }
        // Attach late bytes without reopening the failed upload.
        if (existing.status === 'failed' && !existing.hasBlob) {
          await dbV2.chunks.put({ ...existing, hasBlob: true, updatedAt: Date.now() });
          await dbV2.chunkBlobs.put({
            sessionId: meta.sessionId,
            recordingId: meta.recordingId,
            index: meta.index,
            blob: stored,
          });
          return;
        }
        logger.warn({
          event_type: 'CHUNK_INDEX_COLLISION',
          details: { ...keyDetails(meta), existingStatus: existing.status },
        });
        // Never downgrade settled → pending: no duplicate uploads.
        if (isSettled(existing.status)) return;
      }

      await dbV2.chunks.put({
        ...meta,
        status: 'pending',
        wasMissingChunk: false,
        attempts: 0,
        updatedAt: Date.now(),
        hasBlob: true,
      });
      await dbV2.chunkBlobs.put({
        sessionId: meta.sessionId,
        recordingId: meta.recordingId,
        index: meta.index,
        blob: stored,
      });
    })
  );
  parent.afterCommit();
};

/**
 * Metadata-only fallback when the blob can't persist (e.g. quota): keeps the
 * in-flight upload visible to progress; no durable bytes ⇒ abandoned on failure.
 */
export const addChunkMetadataOnly = async (meta: ChunkMetaInput) => {
  const epoch = captureEpoch();
  if (mirrorAllowed(epoch)) rememberChunkStatus(meta, 'pending');
  const parent = parentGuardFor(meta, 'chunk-metadata');
  await withReopen(() =>
    dbV2.transaction('rw', dbV2.recordings, dbV2.chunks, async () => {
      assertWriteAllowed(epoch);
      if (!(await parent.accept())) return;
      const existing = await dbV2.chunks.get(chunkPk(meta));
      if (existing && isSettled(existing.status)) return;
      await dbV2.chunks.put({
        ...meta,
        status: 'pending',
        wasMissingChunk: false,
        attempts: existing?.attempts ?? 0,
        updatedAt: Date.now(),
        hasBlob: false,
      });
    })
  );
  parent.afterCommit();
};

export const markChunkUploaded = (
  meta: ChunkMetaInput,
  opts: { deleteBlob: boolean }
) => {
  const epoch = captureEpoch();
  if (mirrorAllowed(epoch)) rememberChunkStatus(meta, 'uploaded');
  return withReopen(() =>
    dbV2.transaction('rw', dbV2.recordings, dbV2.chunks, dbV2.chunkBlobs, async () => {
      assertWriteAllowed(epoch);
      let row = await dbV2.chunks.get(chunkPk(meta));
      const reAdding = !row;

      if (!row) {
        // Upload succeeded but the row is gone — re-add, but only under an
        // existing recording (a straggler must not recreate orphans).
        const recording = await dbV2.recordings.get([meta.sessionId, meta.recordingId]);
        if (!recording) {
          // A memory-backed recording never persisted its row, so every chunk
          // misses once. Keyed per recording, so a durable one still warns.
          logStorePersistFailure(
            { event_type: 'CHUNK_MARK_ORPHANED', details: keyDetails(meta) },
            { memoryOnly: isMemoryBackedRecording(meta), level: 'warn' }
          );
          return;
        }
        row = {
          ...meta,
          status: 'pending',
          wasMissingChunk: true,
          attempts: 0,
          updatedAt: Date.now(),
          hasBlob: false,
        };
      }

      const deleteBlob = opts.deleteBlob && row.hasBlob;
      if (deleteBlob) await dbV2.chunkBlobs.delete(chunkPk(meta));
      const keepSyntheticGap = row.syntheticGap && !opts.deleteBlob;
      // Once upload provides real metadata, retain the marker only when a late
      // persist is still allowed to attach bytes for retainData.
      const withoutSyntheticGap = { ...row };
      delete withoutSyntheticGap.syntheticGap;
      await dbV2.chunks.put({
        ...withoutSyntheticGap,
        ...meta,
        status: 'uploaded',
        wasMissingChunk: row.syntheticGap ? false : row.wasMissingChunk,
        updatedAt: Date.now(),
        hasBlob: deleteBlob ? false : row.hasBlob,
        ...(keepSyntheticGap ? { syntheticGap: true } : {}),
      });
      // After the write, not before: a refused put would report a re-add for a
      // chunk that was never added.
      if (reAdding) {
        logger.info({ event_type: 'PROCESS_CHUNK_RE_ADDED', details: keyDetails(meta) });
      }
    })
  );
};

export const markChunkFailed = (meta: ChunkMetaInput) => {
  const epoch = captureEpoch();
  if (mirrorAllowed(epoch)) rememberChunkStatus(meta, 'failed');
  return withReopen(() =>
    dbV2.transaction('rw', dbV2.recordings, dbV2.chunks, async () => {
      assertWriteAllowed(epoch);
      const row = await dbV2.chunks.get(chunkPk(meta));
      if (!row) {
        // Leave a tombstone for a racing persist; hydrate abandons it if
        // bytes never arrive.
        const recording = await dbV2.recordings.get([meta.sessionId, meta.recordingId]);
        if (!recording) {
          logStorePersistFailure(
            { event_type: 'CHUNK_FAILED_NO_ROW', details: keyDetails(meta) },
            { memoryOnly: isMemoryBackedRecording(meta), level: 'warn' }
          );
          return;
        }
        await dbV2.chunks.put({
          ...meta,
          status: 'failed',
          wasMissingChunk: false,
          attempts: 1,
          updatedAt: Date.now(),
          hasBlob: false,
        });
        return;
      }
      if (row.status === 'uploaded') {
        // Never downgrade a settled upload — a duplicate dispatch in another
        // tab can exhaust retries after this tab already uploaded the chunk.
        logger.warn({
          event_type: 'CHUNK_FAILED_AFTER_UPLOADED',
          details: keyDetails(meta),
        });
        return;
      }
      await dbV2.chunks.put({
        ...row,
        ...meta,
        status: row.hasBlob || row.syntheticGap ? 'failed' : 'abandoned',
        attempts: row.attempts + 1,
        updatedAt: Date.now(),
      });
    })
  );
};

/**
 * Terminal: observed-unrecoverable regardless of metadata claims (vs
 * markChunkFailed = bytes retained). Never downgrades `uploaded`.
 */
export const markChunkAbandoned = (meta: ChunkMetaInput) => {
  const epoch = captureEpoch();
  if (mirrorAllowed(epoch)) rememberChunkStatus(meta, 'abandoned');
  return withReopen(() =>
    dbV2.transaction('rw', dbV2.chunks, dbV2.chunkBlobs, async () => {
      assertWriteAllowed(epoch);
      const row = await dbV2.chunks.get(chunkPk(meta));
      if (row?.status === 'uploaded') return;
      await dbV2.chunkBlobs.delete(chunkPk(meta));
      await dbV2.chunks.put({
        ...(row ?? { ...meta, wasMissingChunk: false, attempts: 0 }),
        status: 'abandoned',
        updatedAt: Date.now(),
        hasBlob: false,
      });
    })
  );
};

/** Session hard-deleted server-side (404): abandon everything non-settled at once. */
export const markRecordingAbandoned = (key: RecordingKey) => {
  const epoch = captureEpoch();
  rememberRecordingAbandoned(key);
  forgetRecordingRow(key);
  forgetRetainedRecording(key);
  return withReopen(() =>
    dbV2.transaction(
      'rw',
      dbV2.chunks,
      dbV2.chunkBlobs,
      dbV2.backups,
      dbV2.backupBlobs,
      async () => {
        assertWriteAllowed(epoch);
        const chunks = await dbV2.chunks
          .where('[sessionId+recordingId]')
          .equals([key.sessionId, key.recordingId])
          .toArray();
        const toAbandon = chunks.filter((c) => !isFinal(c.status));

        await Promise.all(
          toAbandon.map(async (c) => {
            if (c.hasBlob) await dbV2.chunkBlobs.delete(chunkPk(c));
            await dbV2.chunks.put({
              ...c,
              status: 'abandoned',
              updatedAt: Date.now(),
              hasBlob: false,
            });
          })
        );

        const backup = await dbV2.backups.get(recordingPk(key));
        if (backup && !isFinal(backup.status)) {
          if (backup.hasBlob) await dbV2.backupBlobs.delete(recordingPk(key));
          await dbV2.backups.put({
            ...backup,
            status: 'abandoned',
            updatedAt: Date.now(),
            hasBlob: false,
          });
        }
      }
    )
  );
};

export interface PendingChunk {
  chunk: ChunkRow;
  recording: RecordingRow;
}

export interface RecoveryRowFilter {
  userRefId: string;
  /**
   * Newest chunk write a recording may carry and still be recovered: a live
   * recorder writes inside this window, a crashed one stopped instantly. A
   * stopped, fully-indexed row bypasses it only with an idempotent delivery.
   */
  inactiveSince?: number;
  /** Told the newest write of each recording `inactiveSince` kept out. */
  onDeferred?: (lastActivityAt: number) => void;
}

const matchesRecoveryOwner = (
  recording: RecordingRow,
  filter?: RecoveryRowFilter
): boolean => !filter || recording.userRefId === filter.userRefId;

/**
 * Keeps only rows whose recording is recoverable, resolving each recording's
 * liveness once however many of its rows are pending.
 */
const selectRecoverable = async <T extends { recording: RecordingRow }>(
  joined: T[],
  filter?: RecoveryRowFilter
): Promise<T[]> => {
  const owned = joined.filter(({ recording }) => matchesRecoveryOwner(recording, filter));
  if (!filter || filter.inactiveSince === undefined) return owned;

  const verdicts = new Map<string, boolean>();
  const recoverable: T[] = [];
  for (const entry of owned) {
    const pair = `${entry.recording.sessionId} ${entry.recording.recordingId}`;
    if (!verdicts.has(pair)) {
      const activeAt = await stillActiveAt(entry.recording, filter.inactiveSince);
      if (activeAt !== null) filter.onDeferred?.(activeAt);
      verdicts.set(pair, activeAt === null);
    }
    if (verdicts.get(pair)) recoverable.push(entry);
  }
  return recoverable;
};

// Failed dispatches a row without an idempotent delivery may carry and still be
// re-sent: a timed-out request may have landed, so each re-send risks a duplicate.
export const RECOVERY_MAX_ROW_DISPATCHES = 3;

const dropCapped = <
  T extends { row: { status: string; attempts: number }; recording: RecordingRow },
>(
  rows: T[],
  kind: 'chunk' | 'backup',
  isIdempotent: (recording: RecordingRow) => boolean
): T[] => {
  const kept = rows.filter(
    ({ row, recording }) =>
      row.status !== 'failed' ||
      isIdempotent(recording) ||
      row.attempts < RECOVERY_MAX_ROW_DISPATCHES
  );
  if (kept.length < rows.length) {
    logger.warn({
      event_type: 'RECOVERY_ROW_DISPATCH_CAP_REACHED',
      details: { kind, rows: rows.length - kept.length },
    });
  }
  return kept;
};

export const getPendingChunks = (filter?: RecoveryRowFilter): Promise<PendingChunk[]> =>
  withReopen(async () => {
    const chunks = await dbV2.chunks.where('status').anyOf('pending', 'failed').toArray();
    const joined = await joinWithRecordings(chunks, 'PENDING_CHUNK_ORPHANED');
    const recoverable = await selectRecoverable(joined, filter);
    return dropCapped(recoverable, 'chunk', hasIdempotentChunkDelivery).map(
      ({ row, recording }) => ({ chunk: row, recording })
    );
  });

export const getChunkBlob = (key: ChunkKey): Promise<Blob | undefined> =>
  withReopen(async () => {
    const row = await dbV2.chunkBlobs.get(chunkPk(key));
    return row ? unpackBlob(row.blob) : undefined;
  });

// ---------------------------------------------------------------------------
// Backups
// ---------------------------------------------------------------------------

export const addBackup = async (key: RecordingKey, blob: Blob) => {
  const epoch = captureEpoch();
  const stored = await packBlob(blob); // outside txn — see addChunk
  const parent = parentGuardFor(key, 'backup');
  await withReopen(() =>
    dbV2.transaction('rw', dbV2.recordings, dbV2.backups, dbV2.backupBlobs, async () => {
      assertWriteAllowed(epoch);
      if (!(await parent.accept())) return;
      const existing = await dbV2.backups.get(recordingPk(key));
      if (existing && isSettled(existing.status)) {
        logger.warn({ event_type: 'BACKUP_COLLISION', details: keyDetails(key) });
        return;
      }
      await dbV2.backups.put({
        sessionId: key.sessionId,
        recordingId: key.recordingId,
        status: 'pending',
        size: blob.size,
        attempts: existing?.attempts ?? 0,
        updatedAt: Date.now(),
        hasBlob: true,
      });
      await dbV2.backupBlobs.put({
        sessionId: key.sessionId,
        recordingId: key.recordingId,
        blob: stored,
      });
    })
  );
  parent.afterCommit();
};

export const markBackupUploaded = (key: RecordingKey, opts: { deleteBlob: boolean }) => {
  const epoch = captureEpoch();
  return withReopen(() =>
    dbV2.transaction('rw', dbV2.backups, dbV2.backupBlobs, async () => {
      assertWriteAllowed(epoch);
      const row = await dbV2.backups.get(recordingPk(key));
      if (!row) {
        logStorePersistFailure(
          { event_type: 'BACKUP_MARK_ORPHANED', details: keyDetails(key) },
          { memoryOnly: isMemoryBackedRecording(key), level: 'warn' }
        );
        return;
      }
      const deleteBlob = opts.deleteBlob && row.hasBlob;
      if (deleteBlob) await dbV2.backupBlobs.delete(recordingPk(key));
      await dbV2.backups.put({
        ...row,
        status: 'uploaded',
        updatedAt: Date.now(),
        hasBlob: deleteBlob ? false : row.hasBlob,
      });
    })
  );
};

export const markBackupFailed = (key: RecordingKey) => {
  const epoch = captureEpoch();
  return withReopen(() =>
    dbV2.transaction('rw', dbV2.backups, async () => {
      assertWriteAllowed(epoch);
      const row = await dbV2.backups.get(recordingPk(key));
      if (!row) {
        logStorePersistFailure(
          { event_type: 'BACKUP_FAILED_NO_ROW', details: keyDetails(key) },
          { memoryOnly: isMemoryBackedRecording(key), level: 'warn' }
        );
        return;
      }
      await dbV2.backups.put({
        ...row,
        status: row.hasBlob ? 'failed' : 'abandoned',
        attempts: row.attempts + 1,
        updatedAt: Date.now(),
      });
    })
  );
};

/**
 * Terminal backup twin of markChunkAbandoned: physical blob deleted
 * regardless of metadata claims, `uploaded` never downgraded, hasBlob false.
 */
export const markBackupAbandoned = (key: RecordingKey) => {
  const epoch = captureEpoch();
  return withReopen(() =>
    dbV2.transaction('rw', dbV2.backups, dbV2.backupBlobs, async () => {
      assertWriteAllowed(epoch);
      const row = await dbV2.backups.get(recordingPk(key));
      if (row?.status === 'uploaded') {
        await dbV2.backupBlobs.delete(recordingPk(key));
        return;
      }
      await dbV2.backupBlobs.delete(recordingPk(key));
      await dbV2.backups.put({
        ...(row ?? {
          sessionId: key.sessionId,
          recordingId: key.recordingId,
          size: 0,
          attempts: 0,
        }),
        status: 'abandoned',
        updatedAt: Date.now(),
        hasBlob: false,
      });
    })
  );
};

export interface PendingBackup {
  backup: BackupRow;
  recording: RecordingRow;
}

export const getPendingBackups = (filter?: RecoveryRowFilter): Promise<PendingBackup[]> =>
  withReopen(async () => {
    const backups = await dbV2.backups
      .where('status')
      .anyOf('pending', 'failed')
      .toArray();
    const joined = await joinWithRecordings(backups, 'PENDING_BACKUP_ORPHANED');
    const recoverable = await selectRecoverable(joined, filter);
    // No backup upload is known to be idempotent.
    return dropCapped(recoverable, 'backup', () => false).map(({ row, recording }) => ({
      backup: row,
      recording,
    }));
  });

export const getBackupBlob = (key: RecordingKey): Promise<Blob | undefined> =>
  withReopen(async () => {
    const row = await dbV2.backupBlobs.get(recordingPk(key));
    return row ? unpackBlob(row.blob) : undefined;
  });

// ---------------------------------------------------------------------------
// Progress, cleanup, wipe
// ---------------------------------------------------------------------------

export const getRecordingProgress = (key: RecordingKey): Promise<RecordingProgress> =>
  withReopen(async () => {
    const chunks = await dbV2.chunks
      .where('[sessionId+recordingId]')
      .equals([key.sessionId, key.recordingId])
      .toArray();
    return aggregateRecordingProgress(chunks);
  });

/** Complete once the final index is known and every expected chunk is settled. */
export const isRecordingUploadComplete = (key: RecordingKey): Promise<boolean> =>
  withReopen(async () => {
    const recording = await dbV2.recordings.get(recordingPk(key));
    if (!recording) return false;
    const chunks = await dbV2.chunks
      .where('[sessionId+recordingId]')
      .equals([key.sessionId, key.recordingId])
      .toArray();
    return isUploadCompleteFrom(recording.lastChunkIndex, chunks);
  });

/**
 * Drops what only this page holds. Post-commit at every call site: an aborted
 * cascade must leave the in-memory recovery state the drain still needs.
 */
const forgetRecordingInMemory = (key: RecordingKey) => {
  forgetRecordingRow(key);
  forgetRecordingChunks(key);
  forgetRetainedRecording(key);
  forgetParentGuardSuppression(key);
};

/** Deletes a recording and related rows within the caller's transaction. */
const deleteRecordingCascade = async (key: RecordingKey) => {
  await dbV2.recordings.delete(recordingPk(key));
  await chunkRangeOf(dbV2.chunks, key).delete();
  await chunkRangeOf(dbV2.chunkBlobs, key).delete();
  await dbV2.backups.delete(recordingPk(key));
  await dbV2.backupBlobs.delete(recordingPk(key));
};

export const deleteRecording = async (key: RecordingKey) => {
  const epoch = captureEpoch();
  await withReopen(() =>
    dbV2.transaction(
      'rw',
      dbV2.recordings,
      dbV2.chunks,
      dbV2.chunkBlobs,
      dbV2.backups,
      dbV2.backupBlobs,
      async () => {
        assertWriteAllowed(epoch);
        await deleteRecordingCascade(key);
      }
    )
  );
  forgetRecordingInMemory(key);
};

/** Why a revalidated delete stood down — bounded, so the log stays aggregatable. */
type CleanupSkipReason =
  | 'recording-missing'
  | 'not-finished'
  | 'retained-audio'
  | 'backup-owed'
  | 'chunk-not-final'
  | 'incomplete-sequence'
  | 'backup-not-final';

/** The child rows the bar consults once the recording row alone cannot answer. */
interface SettlementChildren {
  chunks: ChunkRow[];
  backup: BackupRow | undefined;
}

const readSettlementChildren = async (
  key: RecordingKey
): Promise<SettlementChildren> => ({
  chunks: await chunkRangeOf(dbV2.chunks, key).toArray(),
  backup: await dbV2.backups.get(recordingPk(key)),
});

/**
 * The deletion bar, defined once and asked twice: the scan picks candidates
 * with it, then the delete transaction re-asks it where nothing can change the
 * answer mid-delete. `null` means eligible. The caller supplies the child read
 * because the scan runs it outside a transaction and the revalidation inside
 * one; it stays lazy so a row the recording alone disqualifies costs no reads.
 */
const settlementSkipReason = async (
  rec: RecordingRow | undefined,
  readChildren: () => Promise<SettlementChildren>
): Promise<CleanupSkipReason | null> => {
  if (!rec) return 'recording-missing';
  const { lastChunkIndex } = rec;
  if (!rec.endDate || lastChunkIndex === null) return 'not-finished';
  // Before any child read, because no stored row shows it: the retry buffer
  // holds blobs the store never took, and the cascade would destroy the only
  // copy of audio a drain can still deliver. A later pass deletes it once the
  // buffer lets go.
  if (hasDrainableRetainedRecordingChunks(rec)) return 'retained-audio';

  const { chunks, backup } = await readChildren();

  // Ahead of the fast path: a stop detaches its backup persist, so until that
  // lands the retention flag is the only sign a backup is still owed.
  if (rec.backupRequired && !backup) return 'backup-owed';
  // A finished row with no chunk rows and no backup holds no blobs, so its
  // sequence protects nothing — delete rather than warn about it forever.
  if (chunks.length === 0 && !backup) return null;
  if (chunks.some((c) => !isFinal(c.status))) return 'chunk-not-final';

  const finalIndexes = new Set(chunks.map((c) => c.index));
  for (let index = 0; index <= lastChunkIndex; index++) {
    if (!finalIndexes.has(index)) return 'incomplete-sequence';
  }
  if (backup && !isFinal(backup.status)) return 'backup-not-final';
  return null;
};

/** The bar read inside the delete transaction, where the rows cannot move. */
const cleanupSkipReason = async (key: RecordingKey): Promise<CleanupSkipReason | null> =>
  settlementSkipReason(await dbV2.recordings.get(recordingPk(key)), () =>
    readSettlementChildren(key)
  );

/**
 * Cleanup's delete. The scan that picks candidates runs outside a transaction,
 * so a chunk landing after it would be cascade-deleted with its parent — this
 * re-asks the same questions where nothing can change the answer mid-delete.
 */
export const deleteRecordingIfSettled = async (
  key: RecordingKey
): Promise<'deleted' | 'skipped'> => {
  const epoch = captureEpoch();
  const skipped = await withReopen(() =>
    dbV2.transaction(
      'rw',
      dbV2.recordings,
      dbV2.chunks,
      dbV2.chunkBlobs,
      dbV2.backups,
      dbV2.backupBlobs,
      async () => {
        assertWriteAllowed(epoch);
        const reason = await cleanupSkipReason(key);
        if (reason) return reason;
        await deleteRecordingCascade(key);
        return null;
      }
    )
  );
  if (skipped) {
    logger.info({
      event_type: 'CLEANUP_REVALIDATION_SKIPPED',
      details: { reason: skipped },
    });
    return 'skipped';
  }
  forgetRecordingInMemory(key);
  return 'deleted';
};

export const purgeForeignRecordings = async (
  currentUserRefId: string
): Promise<number> => {
  const epoch = captureEpoch();
  const { foreign, kept } = await withReopen(() =>
    dbV2.transaction(
      'rw',
      dbV2.recordings,
      dbV2.chunks,
      dbV2.chunkBlobs,
      dbV2.backups,
      dbV2.backupBlobs,
      async () => {
        assertWriteAllowed(epoch);
        const all = await dbV2.recordings.toArray();
        const foreign = all.filter((row) => row.userRefId !== currentUserRefId);
        for (const row of foreign) {
          await deleteRecordingCascade(row);
        }
        return { foreign, kept: all.length - foreign.length };
      }
    )
  );
  foreign.forEach(forgetRecordingInMemory);
  const purged = foreign.length;
  if (purged > 0) {
    logger.info({
      event_type: 'LEDGER_FOREIGN_RECORDINGS_PURGED',
      details: { purged, kept },
    });
  }
  return purged;
};

/**
 * Applies `settlementSkipReason` — a bar stricter than note-gen's, where failed
 * blocks deletion because its blob is the retained copy of un-uploaded audio.
 * Only picks candidates — `deleteRecordingIfSettled` re-asks inside its own
 * transaction, and that is the answer that counts.
 */
export const deleteFinishedRecordings = async (excludeRecordingId: string | null) => {
  const recordings = await withReopen(() => dbV2.recordings.toArray());
  const deletable: RecordingKey[] = [];

  for (const rec of recordings) {
    if (rec.recordingId === excludeRecordingId) continue;

    // Kept from the reader so the invalid-sequence warn can name the indexes
    // without a second read.
    let read: SettlementChildren | undefined;
    const reason = await settlementSkipReason(rec, async () => {
      read = await withReopen(() => readSettlementChildren(rec));
      return read;
    });

    if (reason === 'incomplete-sequence') {
      logger.warn({
        event_type: 'CLEANUP_RECORDING_INVALID_SEQUENCE',
        details: {
          ...keyDetails(rec),
          lastChunkIndex: rec.lastChunkIndex,
          settledIndexes: [...new Set(read?.chunks.map((c) => c.index))].sort(
            (a, b) => a - b
          ),
        },
      });
      continue;
    }
    if (reason) continue;

    deletable.push({ sessionId: rec.sessionId, recordingId: rec.recordingId });
  }

  if (deletable.length === 0) return;

  logger.debug({
    event_type: 'DELETE_RECORDINGS_START',
    details: { recordingIds: deletable.map((d) => d.recordingId) },
  });
  await Promise.all(deletable.map((key) => deleteRecordingIfSettled(key)));
  logger.debug({ event_type: 'DELETE_RECORDINGS_DONE' });
};

export interface ClearedStats {
  recordings: number;
  chunks: number;
  backups: number;
  audioBytes: number;
}

/**
 * Wipes every v2 table (logout). Legacy tables are cleared by the caller.
 * Returns what was cleared so the logout log can report scope.
 */
export const clearAll = (): Promise<ClearedStats> => {
  const epoch = captureEpoch();
  return withReopen(() =>
    dbV2.transaction(
      'rw',
      dbV2.recordings,
      dbV2.chunks,
      dbV2.chunkBlobs,
      dbV2.backups,
      dbV2.backupBlobs,
      async () => {
        assertWriteAllowed(epoch);
        const [recordings, chunkRows, backupRows] = await Promise.all([
          dbV2.recordings.count(),
          dbV2.chunks.toArray(),
          dbV2.backups.toArray(),
        ]);
        const audioBytes =
          chunkRows.reduce((sum, c) => sum + (c.hasBlob ? c.size : 0), 0) +
          backupRows.reduce((sum, b) => sum + (b.hasBlob ? b.size : 0), 0);
        await Promise.all([
          dbV2.recordings.clear(),
          dbV2.chunks.clear(),
          dbV2.chunkBlobs.clear(),
          dbV2.backups.clear(),
          dbV2.backupBlobs.clear(),
        ]);
        return {
          recordings,
          chunks: chunkRows.length,
          backups: backupRows.length,
          audioBytes,
        };
      }
    )
  );
};

/** Retained-until-logout bytes, logged at hydrate for quota-creep visibility. */
export const getRetainedFailedBytes = () =>
  withReopen(async () => {
    const [chunks, backups] = await Promise.all([
      dbV2.chunks.where('status').equals('failed').toArray(),
      dbV2.backups.where('status').equals('failed').toArray(),
    ]);
    return (
      chunks.reduce((sum, c) => sum + (c.hasBlob ? c.size : 0), 0) +
      backups.reduce((sum, b) => sum + (b.hasBlob ? b.size : 0), 0)
    );
  });

// ---------------------------------------------------------------------------

const joinWithRecordings = async <T extends RecordingKey>(
  rows: T[],
  orphanEvent: string
): Promise<Array<{ row: T; recording: RecordingRow }>> => {
  const pairId = (r: RecordingKey) => `${r.sessionId} ${r.recordingId}`;
  const pairKeys = [...new Map(rows.map((r) => [pairId(r), r])).values()];
  const recordings = await dbV2.recordings.bulkGet(pairKeys.map((r) => recordingPk(r)));
  const byPair = new Map<string, RecordingRow>();
  pairKeys.forEach((r, i) => {
    const rec = recordings[i];
    if (rec) byPair.set(pairId(r), rec);
  });

  const result: Array<{ row: T; recording: RecordingRow }> = [];
  for (const row of rows) {
    const recording = byPair.get(pairId(row));
    if (!recording) {
      logger.warn({ event_type: orphanEvent, details: keyDetails(row) });
      continue;
    }
    result.push({ row, recording });
  }
  return result;
};
